import { useState, useCallback, useEffect } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';

export default function useSessionStorage(key, initialValue) {
  const captureException = useCaptureException();
  // Return latest value
  const getSessionStorage = useCallback(() => {
    const item = window.sessionStorage.getItem(key);
    const latestValue = item ? JSON.parse(item) : initialValue;
    return latestValue;
  }, [initialValue, key]);

  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const initValue = getSessionStorage();
      return initValue;
    } catch (e) {
      return initialValue;
    }
  });

  // Set value to useState and sessionStorage
  const setValue = useCallback(
    (value) => {
      // Save useState
      setStoredValue(value);
      try {
        // Save to session storage
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        // We can't do anything if session storage is not working
      }
    },
    [key]
  );

  // Get value from sessionStorage and set to useState
  const getValue = useCallback(() => {
    try {
      const latestValue = getSessionStorage();
      setStoredValue(latestValue);
      return latestValue;
    } catch (e) {
      captureException(new Error(`Failed to getValue by key: ${key}`), {
        level: 'warning'
      });
      // If session storage is not working, return stored value
      return storedValue;
    }
  }, [getSessionStorage, captureException, key, storedValue]);

  const removeValue = useCallback(() => {
    try {
      // Remove item out of session storage
      window.sessionStorage.removeItem(key);
    } catch (e) {
      // We can't do anything if session storage is not working
    }
  }, [key]);

  useEffect(() => {
    getValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue, getValue, removeValue];
}
